import * as THREE from "three";
import type { Ribbon, VFX } from "../../../../../../art/vfx.js";
import { hqNode } from "../../views/common";
import { TROOP_KITS } from "../kits";
import { KIT_BUDGET, type PartPoint, type TroopFx } from "./types";

/**
 * A troop skin's effects in a level (TroopKit.fx; skins v2 2026-10-21), driven per soldier by views/troops.ts:
 * the lantern's light pooled on the ground (the unit rings' kind-4 glow: no extra draw), a ribbon behind the weapon's
 * tip through each blow and a glint as it lands (the VFX module's pooled ribbons and sprites), and a sparkle now and
 * then while the soldier stands. Nothing here allocates per frame: a soldier's state is made once with its view and
 * reused through the pool, and the kit's colours and specs are compiled once per kit.
 * Gates: `particles` false (Reduced effects, the lowest tiers: KitClock.gates) -> no ribbons or sprites; the light
 * pool stays (it is the lantern's light, not an effect) but holds still under Reduced effects.
 */

/** The light pool's falloff to the ground: the pool sits at the soldier's feet, under the lantern. */
const POOL = { lift: 0.006 } as const;
/** The idle sparkle's and the glint's sprite shapes (radius, rise, life, size). */
const SPARK = { glint: { radius: 0.05, rise: 0.12, life: 0.4, size: 0.09 }, idle: { radius: 0.04, rise: 0.25, life: 0.9, size: 0.06 }, glintRise: 0.05 } as const;

/** One soldier's effect state (made once per view, kept through the pool). */
export interface TroopFxState {
  fx: TroopFx | null;
  weapon: THREE.Object3D | null;
  poolNode: THREE.Object3D | null;
  idleNode: THREE.Object3D | null;
  /** The live trail ribbon, and when the current blow lands (view time; -Infinity when none). */
  ribbon: Ribbon | null;
  blowAt: number;
  glinted: boolean;
  idleIn: number;
  phase: number;
}

interface Compiled {
  readonly pool: THREE.Color | null;
  readonly trail: { width: number; color: [number, number, number]; bright: number; seg?: number } | null;
  readonly glint: { color: [number, number, number]; radius: number; rise: number; life: number; size: number } | null;
  readonly idle: { color: [number, number, number]; radius: number; rise: number; life: number; size: number } | null;
}

const _p = new THREE.Vector3();
const _q = new THREE.Vector3();
const rgb = (hex: number): [number, number, number] => { const c = new THREE.Color(hex); return [c.r, c.g, c.b]; };
const at = (node: THREE.Object3D, p: PartPoint | undefined, out: THREE.Vector3): THREE.Vector3 =>
  p ? node.localToWorld(out.set(p[0], p[1], p[2])) : node.getWorldPosition(out);

export class TroopSkinFx {
  private static readonly compiled = new WeakMap<TroopFx, Compiled>();

  /** The fx of the troop kit whose soldier is `model` (`troop_soldier__royal`), null for the stock soldier. */
  static forModel(model: string): TroopFx | null {
    for (const k of Object.values(TROOP_KITS)) if (k.model === model) return k.fx ?? null;
    return null;
  }

  static state(): TroopFxState {
    return { fx: null, weapon: null, poolNode: null, idleNode: null, ribbon: null, blowAt: -Infinity, glinted: true, idleIn: 0, phase: 0 };
  }

  /** Points `s` at a (new) body of the model `model` (its parts found once, here). */
  static attach(s: TroopFxState, body: THREE.Object3D, model: string): void {
    const fx = TroopSkinFx.forModel(model);
    s.fx = fx;
    s.weapon = fx?.strike ? hqNode(body, model, "weapon") : null;
    s.poolNode = fx?.pool ? hqNode(body, model, fx.pool.node) : null;
    s.idleNode = fx?.idle ? hqNode(body, model, fx.idle.node) : null;
    s.ribbon = null; s.blowAt = -Infinity; s.glinted = true;
    s.phase = Math.random() * Math.PI * 2;
    s.idleIn = fx?.idle ? fx.idle.every[0] + Math.random() * (fx.idle.every[1] - fx.idle.every[0]) : 0;
  }

  /** A swing has started: its blow lands `impactIn` seconds after `now`. */
  static blow(s: TroopFxState, now: number, impactIn: number): void {
    if (!s.fx?.strike) return;
    s.blowAt = now + impactIn;
    s.glinted = false;
  }

  /** The swing was cut short (the soldier walked on, or fell): the trail fades now, no glint. */
  static cancel(s: TroopFxState, fx: VFX | null): void {
    if (s.ribbon && fx) fx.ribbonEnd(s.ribbon);
    s.ribbon = null; s.blowAt = -Infinity; s.glinted = true;
  }

  /**
   * One frame: the trail and glint of a blow in flight, the idle sparkle while `standing`, and the light pool pushed
   * into `rings` at `groundY` (call between the rings' begin and end). `fx` null (no VFX module): pool only.
   */
  static update(s: TroopFxState, now: number, dt: number, fx: VFX | null, particles: boolean, reduced: boolean, standing: boolean,
    rings: { pushGlow(x: number, y: number, z: number, width: number, rgb: THREE.Color, strength: number, fade?: number): void } | null, groundY: number, fade = 1): void {
    const kit = s.fx;
    if (!kit) return;
    const c = TroopSkinFx.compile(kit);
    if (kit.pool && c.pool && s.poolNode && rings) {
      const p = kit.pool, w = at(s.poolNode, p.at, _p);
      const flick = reduced || !p.flicker ? 1 : 1 - p.flicker * 0.5 * (1 + Math.sin(now * (p.hz ?? 3) * Math.PI * 2 + s.phase) * Math.sin(now * 1.7 + s.phase * 2));
      rings.pushGlow(w.x, groundY + POOL.lift, w.z, p.width, c.pool, p.strength * flick, fade);
    }
    const st = kit.strike;
    if (st && s.weapon && s.blowAt > -Infinity) {
      const tr = st.trail;
      if (tr && c.trail && fx && particles) {
        if (now >= s.blowAt - tr.lead && now <= s.blowAt + tr.after) {
          const tip = at(s.weapon, st.tip, _q);
          if (s.ribbon) fx.ribbonPush(s.ribbon, tip); else s.ribbon = fx.ribbonWith(c.trail, tip);
        } else if (now > s.blowAt + tr.after && s.ribbon) { fx.ribbonEnd(s.ribbon); s.ribbon = null; }
      }
      if (!s.glinted && now >= s.blowAt) {
        s.glinted = true;
        if (st.glint && c.glint && fx && particles) {
          const tip = at(s.weapon, st.tip, _q);
          fx.sparkles(tip, Math.min(KIT_BUDGET.FlourishParticles, st.glint.count), c.glint);
          if (st.glint.flash) fx.flash(tip, st.glint.flash, c.glint.color);
        }
      }
      if (s.glinted && !s.ribbon && now > s.blowAt + (tr?.after ?? 0)) s.blowAt = -Infinity;
    }
    const idle = kit.idle;
    if (idle && c.idle && s.idleNode && standing) {
      s.idleIn -= dt;
      if (s.idleIn <= 0) {
        s.idleIn = idle.every[0] + Math.random() * (idle.every[1] - idle.every[0]);
        if (fx && particles) {
          const w = at(s.idleNode, idle.at, _p);
          fx.sparkles(w, Math.min(KIT_BUDGET.IdleParticles, idle.count), c.idle);
        }
      }
    }
  }

  private static compile(kit: TroopFx): Compiled {
    let c = TroopSkinFx.compiled.get(kit);
    if (!c) {
      const tr = kit.strike?.trail;
      c = {
        pool: kit.pool ? new THREE.Color(kit.pool.colour) : null,
        trail: tr ? { width: tr.width, color: rgb(tr.colour), bright: tr.bright, seg: tr.seg } : null,
        glint: kit.strike?.glint ? { color: rgb(kit.strike.glint.colour), ...SPARK.glint } : null,
        idle: kit.idle ? { color: rgb(kit.idle.colour), ...SPARK.idle, rise: kit.idle.kind === "glints" ? SPARK.glintRise : SPARK.idle.rise } : null,
      };
      TroopSkinFx.compiled.set(kit, c);
    }
    return c;
  }
}
